import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { CheckCircle, Sparkles, ArrowRight, Shield, Zap, Star } from 'lucide-react';
import { useT } from '../i18n/useT';
import { trackStep } from '../lib/attribution';
import { useLang } from '../i18n/LanguageContext';
import CallPicker from './CallPicker';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Particle = ({ delay, left, size }) => (
  <div
    className="absolute rounded-full"
    style={{
      left: `${left}%`,
      bottom: '-10px',
      width: size,
      height: size,
      backgroundColor: '#00D4FF',
      opacity: 0,
      animation: `floatUp 3s ease-out ${delay}s infinite`,
    }}
  />
);

const SuccessPage = () => {
  const navigate = useNavigate();
  const { isFr } = useLang();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');
  const [showContent, setShowContent] = useState(false);
  const [callLabel, setCallLabel] = useState('');
  // /v60 : payé sans créneau, on le fait choisir ici (CallPicker).
  const [needsCall, setNeedsCall] = useState(false);
  const sessionId = new URLSearchParams(window.location.search).get('session_id') || '';

  useEffect(() => {
    setTimeout(() => setShowContent(true), 300);
  }, []);

  useEffect(() => {
    if (!sessionId) return undefined;
    let cancelled = false;
    const confirmBooking = async () => {
      for (let attempt = 0; attempt < 4; attempt += 1) {
        try {
          const res = await fetch(`${API_URL}/api/calls/confirm`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ session_id: sessionId }),
          });
          const data = await res.json();
          if (cancelled) return;
          if (data.call_label) setCallLabel(data.call_label);
          // Pré-remplit avec ce qui a été saisi chez Stripe, sans écraser ce que la personne a déjà tapé.
          if (data.customer_email) setEmail((current) => current || data.customer_email);
          if (data.customer_name) setName((current) => current || data.customer_name);
          if (data.reason === 'no_call' && data.call_later) setNeedsCall(true);
          if (data.confirmed || data.reason === 'no_call') return;
        } catch {
          if (cancelled) return;
        }
        await new Promise((resolve) => setTimeout(resolve, 1500));
      }
    };
    confirmBooking();
    return () => { cancelled = true; };
  }, [sessionId]);

  const handleActivate = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    trackStep('onboard_start');
    try {
      const res = await fetch(`${API_URL}/api/auth/create-user`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, email, session_id: sessionId, locale: isFr ? 'fr' : 'en' }),
      });
      const data = await res.json();
      if (res.ok) {
        // Le Worker vérifie le paiement chez Stripe et renvoie un jeton : la personne est connectée tout de suite.
        if (data.access_token && data.user) {
          localStorage.setItem('token', data.access_token);
          localStorage.setItem('user', JSON.stringify(data.user));
        }
        trackStep('onboard_done');
        setDone(true);
      } else {
        setError(data.detail || (isFr ? 'Une erreur est survenue.' : 'An error occurred.'));
      }
    } catch {
      setError(isFr ? 'Erreur de connexion.' : 'Connection error.');
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen relative overflow-hidden flex items-center justify-center px-4" style={{ backgroundColor: '#0a0a0f' }}>
      {/* Animated background */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute inset-0" style={{ background: 'radial-gradient(ellipse at 50% 0%, rgba(0,212,255,0.08) 0%, transparent 60%)' }} />
        <div className="absolute inset-0" style={{ background: 'radial-gradient(ellipse at 20% 80%, rgba(0,102,255,0.05) 0%, transparent 50%)' }} />
        <div className="absolute inset-0" style={{ background: 'radial-gradient(ellipse at 80% 80%, rgba(0,212,255,0.04) 0%, transparent 50%)' }} />
        {/* Floating particles */}
        {[...Array(12)].map((_, i) => (
          <Particle key={i} delay={i * 0.4} left={8 + i * 8} size={`${3 + (i % 3) * 2}px`} />
        ))}
      </div>

      {/* Grid lines */}
      <div className="absolute inset-0 pointer-events-none" style={{ backgroundImage: 'linear-gradient(rgba(0,212,255,0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(0,212,255,0.03) 1px, transparent 1px)', backgroundSize: '60px 60px' }} />

      <div className={`relative z-10 w-full max-w-[480px] transition-all duration-700 ${showContent ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
        {!done ? (
          <>
            {/* Success icon */}
            <div className="flex justify-center mb-6">
              <div className="relative">
                <div className="w-[80px] h-[80px] rounded-2xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg, rgba(0,212,255,0.15) 0%, rgba(0,102,255,0.1) 100%)', border: '1px solid rgba(0,212,255,0.2)', boxShadow: '0 0 40px rgba(0,212,255,0.15)' }}>
                  <CheckCircle size={36} style={{ color: '#00D4FF' }} />
                </div>
                <div className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-green-500 flex items-center justify-center" style={{ boxShadow: '0 0 12px rgba(72,187,120,0.6)' }}>
                  <Sparkles size={10} className="text-white" />
                </div>
              </div>
            </div>

            {/* Title */}
            <h1 className="text-center text-[32px] font-bold text-white mb-2" style={{ fontFamily: '"Space Grotesk", sans-serif' }}>
              {isFr ? 'Paiement confirmé' : 'Payment confirmed'}
              <span style={{ color: '#00D4FF' }}>{isFr ? ' !' : '!'}</span>
            </h1>
            <p className="text-center text-[14px] mb-8" style={{ fontFamily: '"Inter", sans-serif', color: 'rgba(255,255,255,0.5)' }}>
              {callLabel
                ? (isFr ? `Ton appel est réservé : ${callLabel}. Active ton compte pour accéder à tout le reste.` : `Your call is booked: ${callLabel}. Activate your account to access everything else.`)
                : (isFr ? 'Dernière étape : active ton compte pour accéder à tes formations.' : 'Last step: activate your account to access your programs.')}
            </p>

            {/* Features badges */}
            <div className="flex justify-center gap-3 mb-8">
              {[
                { icon: Shield, label: isFr ? '6 formations' : '6 programs' },
                { icon: Zap, label: isFr ? 'Accès immédiat' : 'Instant access' },
                { icon: Star, label: isFr ? 'Communauté privée' : 'Private community' },
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-bold" style={{ backgroundColor: 'rgba(0,212,255,0.06)', border: '1px solid rgba(0,212,255,0.12)', color: '#00D4FF', fontFamily: '"Inter", sans-serif' }}>
                  <item.icon size={12} />
                  {item.label}
                </div>
              ))}
            </div>

            {/* Form */}
            <form onSubmit={handleActivate} data-testid="activate-form" className="rounded-2xl p-6" style={{ backgroundColor: 'rgba(255,255,255,0.02)', border: '1px solid rgba(0,212,255,0.08)', backdropFilter: 'blur(8px)' }}>
              <div className="space-y-4">
                <div>
                  <label className="block text-[12px] font-bold uppercase tracking-wider mb-2" style={{ color: 'rgba(0,212,255,0.7)', fontFamily: '"Inter", sans-serif' }}>
                    {isFr ? 'Prénom' : 'First name'}
                  </label>
                  <input
                    type="text" required value={name} onChange={(e) => setName(e.target.value)}
                    data-testid="activate-name"
                    className="w-full px-4 py-3 rounded-lg text-[14px] text-white focus:outline-none transition-all duration-200"
                    placeholder={isFr ? 'Ton prénom' : 'Your first name'}
                    style={{ backgroundColor: 'rgba(0,0,0,0.4)', border: '1px solid rgba(0,212,255,0.1)', fontFamily: '"Inter", sans-serif' }}
                    onFocus={(e) => e.target.style.borderColor = 'rgba(0,212,255,0.4)'}
                    onBlur={(e) => e.target.style.borderColor = 'rgba(0,212,255,0.1)'}
                  />
                </div>
                <div>
                  <label className="block text-[12px] font-bold uppercase tracking-wider mb-2" style={{ color: 'rgba(0,212,255,0.7)', fontFamily: '"Inter", sans-serif' }}>
                    Email
                  </label>
                  <input
                    type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                    data-testid="activate-email"
                    className="w-full px-4 py-3 rounded-lg text-[14px] text-white focus:outline-none transition-all duration-200"
                    placeholder={isFr ? 'ton@email.com' : 'your@email.com'}
                    style={{ backgroundColor: 'rgba(0,0,0,0.4)', border: '1px solid rgba(0,212,255,0.1)', fontFamily: '"Inter", sans-serif' }}
                    onFocus={(e) => e.target.style.borderColor = 'rgba(0,212,255,0.4)'}
                    onBlur={(e) => e.target.style.borderColor = 'rgba(0,212,255,0.1)'}
                  />
                </div>
              </div>

              {error && <p data-testid="activate-error" className="mt-3 text-red-400 text-[13px] text-center" style={{ fontFamily: '"Inter", sans-serif' }}>{error}</p>}

              <button
                type="submit" disabled={loading} data-testid="activate-submit"
                className="w-full mt-5 py-3.5 rounded-lg font-bold text-[15px] transition-all duration-300 flex items-center justify-center gap-2 group"
                style={{ background: 'linear-gradient(135deg, #00D4FF 0%, #0066FF 100%)', color: '#000000', fontFamily: '"Inter", sans-serif', opacity: loading ? 0.6 : 1, boxShadow: '0 4px 20px rgba(0,212,255,0.25)' }}
              >
                {loading ? (isFr ? 'Activation...' : 'Activating...') : (
                  <>
                    {isFr ? 'Activer mon compte' : 'Activate my account'}
                    <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                  </>
                )}
              </button>
            </form>
          </>
        ) : (
          /* ─── ACCOUNT ACTIVATED ─── */
          <div className="text-center" data-testid="activate-success">
            <div className="relative mx-auto mb-6 w-[100px] h-[100px]">
              <div className="absolute inset-0 rounded-full animate-ping" style={{ backgroundColor: 'rgba(0,212,255,0.1)' }} />
              <div className="relative w-full h-full rounded-full flex items-center justify-center" style={{ background: 'linear-gradient(135deg, rgba(0,212,255,0.2) 0%, rgba(0,102,255,0.15) 100%)', border: '2px solid rgba(0,212,255,0.3)', boxShadow: '0 0 60px rgba(0,212,255,0.2)' }}>
                <CheckCircle size={44} style={{ color: '#00D4FF' }} />
              </div>
            </div>
            <h1 className="text-[32px] font-bold text-white mb-3" style={{ fontFamily: '"Space Grotesk", sans-serif' }}>
              {isFr ? 'Bienvenue dans OpenBro' : 'Welcome to OpenBro'}
              <span style={{ color: '#00D4FF' }}>{isFr ? ' !' : '!'}</span>
            </h1>
            <p className="text-[15px] max-w-sm mx-auto mb-8" style={{ fontFamily: '"Inter", sans-serif', color: 'rgba(255,255,255,0.5)' }}>
              {isFr ? 'Ton compte est actif et tu es connecté. Pour revenir plus tard, connecte-toi avec ce même email : tu recevras un code.' : 'Your account is active and you are logged in. To come back later, log in with this same email: you will get a code.'}
            </p>

            {needsCall && !callLabel && (
              <CallPicker sessionId={sessionId} lang={isFr ? 'fr' : 'en'} onBooked={(label) => { setCallLabel(label); setNeedsCall(false); }} />
            )}
            {callLabel && (
              <p className="mb-6 text-[14px]" style={{ color: '#4ade80', fontFamily: '"Inter", sans-serif' }}>
                {isFr ? `Ton appel est réservé : ${callLabel}.` : `Your call is booked: ${callLabel}.`}
              </p>
            )}

            {/* What you get */}
            <div className="space-y-3 mb-8 max-w-sm mx-auto text-left">
              {[
                { icon: '⚡', text: isFr ? '6 formations, plus de 100 leçons' : '6 programs, over 100 lessons' },
                { icon: '👥', text: isFr ? 'La communauté privée sur Telegram' : 'The private Telegram community' },
                { icon: '🎯', text: isFr ? 'Le suivi de tes habitudes' : 'Habit tracking' },
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-3 px-4 py-3 rounded-xl" style={{ backgroundColor: 'rgba(0,212,255,0.04)', border: '1px solid rgba(0,212,255,0.08)' }}>
                  <span className="text-[18px]">{item.icon}</span>
                  <span className="text-[13px] text-white" style={{ fontFamily: '"Inter", sans-serif' }}>{item.text}</span>
                </div>
              ))}
            </div>

            <button
              onClick={() => (localStorage.getItem('token') ? window.location.assign('/dashboard') : navigate('/login'))} data-testid="go-to-login"
              className="w-full py-3.5 rounded-lg font-bold text-[15px] transition-all duration-300 flex items-center justify-center gap-2 group"
              style={{ background: 'linear-gradient(135deg, #00D4FF 0%, #0066FF 100%)', color: '#000000', fontFamily: '"Inter", sans-serif', boxShadow: '0 4px 20px rgba(0,212,255,0.25)' }}
            >
              {isFr ? 'Accéder à mon espace' : 'Go to my space'}
              <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
            </button>
          </div>
        )}

        <p className="text-center mt-6">
          <Link to="/" className="text-[13px] hover:text-white transition-colors" style={{ fontFamily: '"Inter", sans-serif', color: 'rgba(255,255,255,0.3)' }}>
            {isFr ? '← Retour à l\'accueil' : '← Back to home'}
          </Link>
        </p>
      </div>

      <style>{`
        @keyframes floatUp {
          0% { opacity: 0; transform: translateY(0) scale(1); }
          30% { opacity: 0.6; }
          100% { opacity: 0; transform: translateY(-100vh) scale(0.3); }
        }
      `}</style>
    </div>
  );
};

export default SuccessPage;
